/*
 * Mirrors https://github.com/skyslope/files-ui/blob/dev/src/utils/frame-origin.ts
 */
import { isAppRunningInIframe } from './is-app-running-in-iframe';

/**
 * `?frameOrigin=<origin>` carries the origin of the page framing us — the partner page the
 * SkySlope Forms widget runs in. The gateway's frame-ancestors filter validates it against the
 * partner whitelist and allows that origin to frame the response.
 *
 * The sender is reached by a full-page navigation from Forms inside the widget's iframe, so the
 * frame stays a child of the partner page while the document becomes ours. The gateway cannot
 * infer the partner from the Referer (that is Forms, which resolves to `*.skyslope.com`), and the
 * `x-referrers` cookie it would otherwise fall back on is a third-party cookie that Safari,
 * Chrome incognito and Firefox strict all block. The param is the only channel that survives.
 *
 * We read the value once at boot, keep it for the life of this document, and re-append it on the
 * full-page loads we issue back to Forms. In-app router navigation never refetches a document, but
 * a later reload of the frame does, so `RetainFrameOrigin` keeps the param in the address bar too.
 *
 * Lifecycle: the value is only meaningful while we are framed. A standalone (top-level) load
 * forgets any stored value, so a tab that later opens the sender directly never carries a partner
 * origin from an earlier widget session.
 */
export const FRAME_ORIGIN_PARAM = 'frameOrigin';

const FRAME_ORIGIN_STORAGE_KEY = 'frameOrigin';

// In-memory copy for this document, so a browser where sessionStorage throws (blocked storage)
// still carries the origin present in the entry URL through to the next full-page load.
let rememberedFrameOrigin: string | null = null;

/** Accepts only an http(s) URL and returns just its origin (no path, no trailing slash). */
export function normalizeFrameOrigin(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/** Forgets any remembered origin (standalone load, or tests). */
export function forgetFrameOrigin(): void {
  rememberedFrameOrigin = null;
  try {
    sessionStorage.removeItem(FRAME_ORIGIN_STORAGE_KEY);
  } catch {
    // Storage blocked: nothing stored there to forget.
  }
}

/** Reads `frameOrigin` from the entry URL and remembers it. Call once at boot. */
export function captureFrameOrigin(search: string = window.location.search): void {
  if (!isAppRunningInIframe()) {
    forgetFrameOrigin();
    return;
  }
  const frameOrigin = normalizeFrameOrigin(new URLSearchParams(search).get(FRAME_ORIGIN_PARAM));
  if (!frameOrigin) return;
  rememberedFrameOrigin = frameOrigin;
  try {
    sessionStorage.setItem(FRAME_ORIGIN_STORAGE_KEY, frameOrigin);
  } catch {
    // Storage blocked: the in-memory copy still serves this document.
  }
}

export function getFrameOrigin(): string | null {
  if (!isAppRunningInIframe()) return null;
  if (rememberedFrameOrigin) return rememberedFrameOrigin;
  try {
    return normalizeFrameOrigin(sessionStorage.getItem(FRAME_ORIGIN_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Re-appends the remembered frameOrigin to a URL we are about to load in this frame, so the
 * partner is presented to the gateway again. Used for the hops back to Forms, which are a
 * different origin from ours — unlike the files-ui helper, this one does not refuse cross-origin.
 *
 * Returns the href unchanged when there is nothing to add, when it already carries the param, or
 * when it is not an http(s) document.
 */
export function withFrameOrigin(href: string): string {
  const frameOrigin = getFrameOrigin();
  if (!frameOrigin) return href;

  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return href;
  }
  // Only real http(s) documents. A blob: URL reports its inner origin but must not be rewritten.
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return href;
  if (
    url.searchParams.get(FRAME_ORIGIN_PARAM) === frameOrigin &&
    url.searchParams.getAll(FRAME_ORIGIN_PARAM).length === 1
  ) {
    return href;
  }

  // set() overwrites a stale or repeated value with the one remembered for this frame
  url.searchParams.set(FRAME_ORIGIN_PARAM, frameOrigin);

  // Keep path-style hrefs path-style; only an absolute input comes back absolute.
  const isAbsolute = /^https?:\/\//i.test(href);
  return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}
